import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef } from "react";
import { Button } from "../../Components/OnBoarding/Button";
import { Card, CardContent } from "../../Components/OnBoarding/Card";
import Host from "../../assets/OnBoarding/Host.svg";
import Stay from "../../assets/OnBoarding/Stay.svg";
import Coin from "../../assets/OnBoarding/Coin.svg";
import Lock from "../../assets/OnBoarding/Lock.svg";
import House from "../../assets/OnBoarding/House.svg";
import Bag from "../../assets/OnBoarding/Bag.svg";
import { setConnectModal } from "../../ReduxSlices/ModalSlice";
import Footer from "./Footer";
import Header from "./Header";
import Nibru from "../../assets/images/Nibirulogo.svg";
import { PurpleButton } from "../../Components/Buttons/PurpleButton";
import { WhiteButton } from "../../Components/Buttons/WhiteButton";
import propertyService from "../../Services/PropertyService";
import initialLoadOptimizer from "../../Services/InitialLoadOptimizer";
import { selectAllProperties } from "../../ReduxSlices/RentalPropertySlice";
import mapPinIcon from "../../assets/images/Dashboard/Dashboard/mappin.svg";

export default function CodedEstateLanding() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const properties = useSelector(selectAllProperties);
  const preloadStartedRef = useRef(false);

  // Comprehensive preloading function for Rent.js page
  const preloadRentPageAssets = async (loadedProperties) => {
    // Prevent duplicate preloading
    if (preloadStartedRef.current) {
      console.log('OnBoarding - Preloading already started, skipping...');
      return;
    }
    
    if (!loadedProperties || loadedProperties.length === 0) {
      console.log('OnBoarding - No properties to preload');
      return;
    }

    preloadStartedRef.current = true;
    
    try {
      console.log('🚀 OnBoarding - Starting comprehensive Rent page preloading...');
      
      // 1. Preload all property images
      if (Array.isArray(loadedProperties) && loadedProperties.length > 0) {
        console.log(`📸 Preloading images for ${loadedProperties.length} properties...`);
        
        // Collect all property images
        const allPropertyImages = [];
        loadedProperties.forEach(property => {
          if (property?.metaData?.images && Array.isArray(property.metaData.images)) {
            // Preload first 3 images of each property (most important)
            property.metaData.images.slice(0, 3).forEach(img => {
              if (img && !allPropertyImages.includes(img)) {
                allPropertyImages.push(img);
              }
            });
          }
        });

        // Preload above-the-fold images immediately (first 6 properties)
        if (allPropertyImages.length > 0) {
          await initialLoadOptimizer.preloadAboveTheFold(loadedProperties);
          
          // Preload remaining images in background with lower priority
          setTimeout(async () => {
            await initialLoadOptimizer.preloadBackgroundImages(loadedProperties);
            
            // Preload additional images in batches
            const remainingImages = allPropertyImages.slice(6);
            if (remainingImages.length > 0) {
              console.log(`🔄 Preloading ${remainingImages.length} additional property images...`);
              await initialLoadOptimizer.preloadCriticalImages(remainingImages, {
                maxConcurrent: 3,
                quality: 50,
                width: 400,
                height: 300,
                timeout: 15000
              });
            }
          }, 2000);
        }
      }

      // 2. Preload critical component assets (icons, images used in Rent.js)
      const criticalAssets = [
        mapPinIcon,
        // Add other critical assets if needed
      ];

      console.log('🎨 Preloading critical component assets...');
      await Promise.allSettled(
        criticalAssets.map(asset => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(asset);
            img.onerror = () => reject(new Error(`Failed to load ${asset}`));
            img.src = asset;
          });
        })
      );

      // 3. Ensure Google Maps API is ready (it's already loaded in index.html, but we can verify)
      if (window.google && window.google.maps) {
        console.log('✅ Google Maps API is ready');
      } else {
        console.log('⏳ Waiting for Google Maps API...');
        // Wait for Google Maps to load
        let attempts = 0;
        const checkGoogleMaps = setInterval(() => {
          attempts++;
          if (window.google && window.google.maps) {
            console.log('✅ Google Maps API loaded');
            clearInterval(checkGoogleMaps);
          } else if (attempts > 50) { // 5 seconds max wait
            console.warn('⚠️ Google Maps API not loaded after 5 seconds');
            clearInterval(checkGoogleMaps);
          }
        }, 100);
      }

      // 4. Preload component modules (lazy load them to ensure they're cached)
      setTimeout(async () => {
        try {
          // Dynamically import Rent.js components to cache them
          await Promise.allSettled([
            import("../../Components/Rent/RentalNavigationComponent"),
            import("../../Components/Rent/Search"),
            import("../../Components/Rent/Listings"),
            import("../../Components/Rent/Filter"),
            import("../../Components/RealEstateProperty/RentalItem"),
            import("../../Components/RealEstateProperty/MiniRentalItem"),
            import("../../Components/Chatbot/Chatbot"),
          ]);
          console.log('✅ Rent page components preloaded');
        } catch (error) {
          console.warn('⚠️ Some components failed to preload:', error);
        }
      }, 3000);

      console.log('✅ OnBoarding - Rent page preloading completed');
    } catch (error) {
      console.warn('⚠️ OnBoarding - Rent page preloading error:', error);
      // Reset flag on error so we can retry if needed
      preloadStartedRef.current = false;
    }
  };

  // Pre-load properties and all Rent.js assets when component mounts
  useEffect(() => {
    const preloadEverything = async () => {
      try {
        let loadedProperties = null;
        
        // Check if properties are already cached
        if (!propertyService.isPropertiesCached()) {
          console.log('OnBoarding - Starting property pre-loading...');
          loadedProperties = await propertyService.preloadProperties(dispatch);
          console.log('OnBoarding - Properties pre-loaded successfully');
        } else {
          console.log('OnBoarding - Properties already cached');
          // Get cached properties for image preloading
          loadedProperties = propertyService.getCachedProperties() || properties;
        }

        // After properties are loaded, preload all Rent.js assets
        if (loadedProperties && loadedProperties.length > 0) {
          // Start preloading Rent page assets immediately
          preloadRentPageAssets(loadedProperties);
        }
      } catch (error) {
        console.warn('OnBoarding - Pre-loading failed:', error);
        // Don't block the user experience if pre-loading fails
      }
    };

    // Start pre-loading after a short delay to not block initial render
    const timer = setTimeout(preloadEverything, 100);
    
    return () => clearTimeout(timer);
  }, [dispatch]);

  // Also trigger preloading when properties are loaded in Redux
  useEffect(() => {
    if (properties && properties.length > 0) {
      console.log(`OnBoarding - Properties loaded in Redux (${properties.length}), triggering asset preload...`);
      // Use a small delay to ensure Redux state is stable
      const timer = setTimeout(() => {
        preloadRentPageAssets(properties);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [properties.length]); // Only trigger when property count changes

  const handleExploreListings = () => {
    dispatch(setConnectModal(true));
    sessionStorage.setItem("postWalletDestination", "/onboarding/steps");
    sessionStorage.setItem("postWalletAction", "explore");
  };

  const handleJustLooking = () => {
    navigate("/onboarding/steps");
  };

  const handleConnectWallet = () => {
    dispatch(setConnectModal(true));
    sessionStorage.setItem("postWalletDestination", "/onboarding/host1");
    sessionStorage.setItem("postWalletAction", "host");
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get Started with Coded Estate
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Choose how you want to experience our blockchain-powered<br />
            real estate platform
          </p>
        </div>

        {/* Responsive Cards Section */}
        <div className="flex flex-col lg:flex-row justify-center items-stretch gap-8 mb-12">
          {/* Host Card */}
          <Card className="w-full max-w-md rounded-[12px] border border-[#E3E3E3] bg-white flex flex-col overflow-hidden shadow-sm">
            <div className="relative h-48 sm:h-56">
              <img src={Host} alt="Host" className="object-cover w-full h-full" />
            </div>
            <CardContent className="p-6 flex-1 flex flex-col">
              <img src={House} alt="Host Icon" className="w-12 h-12 mb-4" />
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Become a Host</h2>
              <p className="text-base font-medium text-gray-900 mb-4">List your property, earn crypto.</p>
              <p className="text-sm sm:text-base text-gray-600 mb-6 flex-1">
                Create listings, manage bookings, and receive payments in cryptocurrency or traditional methods.
              </p>
              <PurpleButton text="Connect Wallet" onClick={handleConnectWallet} />
            </CardContent>
          </Card>

          {/* Stay Card */}
          <Card className="w-full max-w-md rounded-[12px] border border-[#E3E3E3] bg-gradient-to-b from-[#6329EF] to-[#5F22EE] text-white flex flex-col overflow-hidden shadow-sm">
            <div className="relative h-48 sm:h-56">
              <img src={Stay} alt="Stay" className="object-cover w-full h-full" />
            </div>
            <CardContent className="p-6 flex-1 flex flex-col bg-gradient-to-b from-[#6329EF] to-[#5F22EE]">
              <img src={Bag} alt="Stay Icon" className="w-12 h-12 bg-white/20 rounded-full mb-4" />
              <h2 className="text-xl sm:text-2xl font-bold mb-2">Book a Stay</h2>
              <p className="text-base font-medium mb-4">Explore unique stays, pay with crypto.</p>
              <p className="text-sm sm:text-base text-white/90 mb-6 flex-1">
                Discover amazing properties worldwide and book securely using cryptocurrency or traditional payment
                methods.
              </p>
              <WhiteButton
                text="Explore Listings"
                onClick={handleExploreListings}
                className="text-[#5B1DEE] text-center"
              />
            </CardContent>
          </Card>
        </div>

        {/* Blockchain Info Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center gap-6 sm:gap-8 mb-6">
            <img src={Nibru} alt="Logo 1" className="w-8 h-8" />
            <img src={Coin} alt="Logo 2" className="w-8 h-8" />
            <img src={Lock} alt="Logo 3" className="w-8 h-8" />
          </div>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto mb-8">
            Coded Estate uses blockchain technology to provide secure, transparent, and efficient real estate
            transactions. Don't worry — we support both crypto and traditional payment methods.
          </p>
          <Button variant="outline" onClick={handleJustLooking}>Just Looking</Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
